/**
 * ARCADIA '84 — shell entry.
 *
 * Boot order:
 * 1. Find the canvas + 2D context.
 * 2. Run the CRT power-on boot animation (sc-2775).
 * 3. Show the cartridge selector (sc-2818). Player picks a cartridge.
 * 4. Hand off to the launcher; it runs the cartridge until the cartridge
 *    fires ctx.exit(). Loop returns to step 3.
 * 5. Wire up the PWA install funnel (sc-2776) before the selector shows.
 */

import './styles/tokens.css';
import './styles/shell.css';
import { runCallbackIfNeeded } from './auth/callback.js';
import { wireFooterSession } from './auth/footer-session.js';
import { runBootSequence } from './runtime/boot.js';
import {
  createInstallController,
  type InstallController,
  type InstallStatus,
} from './runtime/install.js';
import { getCabinetAudioBus } from './runtime/context.js';
import { KeyboardInputSource } from './runtime/input-keyboard.js';
import { attachCompositor, getOffscreenContext } from './runtime/compositor.js';
import { setCabinetMode } from './runtime/cabinet-mode.js';
import { launchCartridge } from './runtime/launcher.js';
import { runModePicker } from './runtime/mode-picker.js';
import { attachOrientationController } from './runtime/orientation.js';
import { runSelector } from './runtime/selector.js';
import { CARTRIDGES } from './cartridges/registry.js';

function wireInstallFunnel(controller: InstallController): void {
  const banner = document.getElementById('install-banner');
  const acceptBtn = document.getElementById('install-banner-accept');
  const dismissBtn = document.getElementById('install-banner-dismiss');
  const overlay = document.getElementById('install-overlay');
  const overlayDismiss = document.getElementById('install-overlay-dismiss');

  if (
    !(banner instanceof HTMLElement) ||
    !(acceptBtn instanceof HTMLButtonElement) ||
    !(dismissBtn instanceof HTMLButtonElement) ||
    !(overlay instanceof HTMLElement) ||
    !(overlayDismiss instanceof HTMLButtonElement)
  ) {
    console.warn('[arcadia84] install funnel: DOM nodes missing — skipping');
    return;
  }

  const render = (status: InstallStatus): void => {
    banner.hidden = status.surface !== 'banner';
    overlay.hidden = status.surface !== 'ios-instructions';
  };

  acceptBtn.addEventListener('click', () => {
    void controller.install().then((outcome) => {
      console.info('[arcadia84] install outcome', { outcome });
    });
  });

  dismissBtn.addEventListener('click', () => {
    controller.dismiss();
  });

  overlayDismiss.addEventListener('click', () => {
    controller.dismiss();
  });

  render(controller.getStatus());
  controller.subscribe(render);
}

/**
 * Update the cabinet's control-panel title to reflect the active cartridge.
 */
function setCabinetTitle(title: string): void {
  const el = document.getElementById('cabinet-title');
  if (el) {
    el.textContent = title;
  }
}

/**
 * Top-level cabinet loop: selector → cartridge → selector → ...
 *
 * The cabinet doesn't replay the boot animation between cartridges (that
 * was a Sprint 0 question — boot is "cabinet powers on," not "every game
 * starts"). The selector is the persistent post-boot home. Player exits a
 * cartridge → lands back on the selector to pick again.
 */
async function runCabinet(
  ctx2d: CanvasRenderingContext2D,
  input: KeyboardInputSource
): Promise<void> {
  for (;;) {
    // Selector + mode-picker run in `selecting` mode — same chrome-
    // collapse mechanism as `playing`. The canvas's own "SELECT YOUR
    // GAME" header carries the brand; the marquee/control-panel
    // chrome would otherwise crowd the bezel on short tablet aspects
    // and clip the bottom of the selector ("PRESS Z" footer).
    setCabinetMode('selecting');
    setCabinetTitle('SELECT GAME');

    const { module_ } = await runSelector(ctx2d, input, CARTRIDGES);

    // Mode picker (sc-3016): NORMAL vs DAILY CHALLENGE. Escape backs
    // out to the selector for another pick. Stays in `selecting` mode
    // — same canvas-engaged user intent.
    const choice = await runModePicker(ctx2d, input, module_);
    if (choice.kind === 'back') {
      continue;
    }

    await launchCartridge(module_, ctx2d, input, {
      onTitle: (t) => setCabinetTitle(t),
      registerPauseRequest: registerEscapePauseListener,
      ...(choice.mode === 'daily'
        ? { dailyChallengeDate: todayUtcDate() }
        : {}),
    });
    // launchCartridge flips back to attract mode on exit; loop iterates.
  }
}

function todayUtcDate(): string {
  // YYYY-MM-DD in UTC — matches the server's daily-seed convention.
  return new Date().toISOString().slice(0, 10);
}

/**
 * Window-level Escape listener. While a cartridge is running, the first
 * Escape press fires `requestPause`; the launcher pauses the runtime
 * and shows the pause overlay. Subsequent Escape presses are read by
 * the overlay (via the `coin` button mapping) to resume.
 *
 * The launcher passes us a `requestPause` callback unique to the current
 * cartridge run. We return an unregister function it calls in its
 * `finally` block to detach the listener cleanly.
 */
function registerEscapePauseListener(requestPause: () => void): () => void {
  // Throttle re-fires to ~1/sec — the overlay handles its own Escape via
  // the input source's `coin` justPressed. This listener only matters
  // for "open the overlay from gameplay."
  let lastFire = 0;
  const handler = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape') return;
    // Ignore Escape from inside a text input — the login modal (and any
    // future text-entry overlay) handles its own Esc-to-close behavior.
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable)
    ) {
      return;
    }
    const now = performance.now();
    if (now - lastFire < 500) return;
    lastFire = now;
    requestPause();
  };
  window.addEventListener('keydown', handler);
  return () => {
    window.removeEventListener('keydown', handler);
  };
}

async function boot(): Promise<void> {
  // sc-3015: handle /auth/callback before anything else. On a successful
  // exchange, the session is stashed and the URL is rewritten to `/` —
  // the cabinet boots normally below with the player already signed in.
  // On a rendered error (bad/expired token), the callback handler shows
  // its own error card and we bail without booting the cabinet.
  const callback = await runCallbackIfNeeded();
  if (callback.handled && !callback.signedIn) {
    return;
  }

  const canvas = document.getElementById('game');
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('Cabinet missing #game canvas — index.html is broken.');
  }

  // Compositor owns two surfaces: the visible #game canvas (sized to the
  // bezel × devicePixelRatio so the blit lands on native pixels) and an
  // off-screen canvas at the cabinet's logical resolution. All shell +
  // cartridge code renders into the off-screen ctx; the compositor blits
  // it onto the visible canvas every frame (nearest-neighbor for pixel
  // cartridges, bilinear for vector). Eliminates the fractional-pixel
  // artifacts of `image-rendering: pixelated` on the visible canvas.
  attachCompositor(canvas, 480, 270);
  const ctx = getOffscreenContext();

  // Spin up the install controller before the boot animation finishes —
  // the browser may fire beforeinstallprompt early and we don't want to
  // miss the event window.
  const installController = createInstallController();

  // Orientation controller attaches before boot so the rotate-to-play
  // overlay can appear immediately if the user lands on portrait.
  attachOrientationController();

  // Keyboard input attaches once at cabinet boot; survives across cartridge
  // teardowns / restarts.
  const input = new KeyboardInputSource();
  input.attach();

  // Cabinet-level mute toggle. M flips audio mute regardless of cartridge
  // state — works during gameplay, in pause overlay, on the selector, on
  // game-over. Single source of truth: the shared audio bus.
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyM') return;
    // Ignore if a text input has focus (none in the cabinet today, but
    // safe default for any future settings overlay).
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    const audio = getCabinetAudioBus();
    audio.setMuted(!audio.getMuted());
    e.preventDefault();
  });

  const result = await runBootSequence(ctx);
  wireInstallFunnel(installController);
  wireFooterSession();

  console.info('[arcadia84] cabinet booted', {
    canvas: `${canvas.width}x${canvas.height}`,
    skipped: result.skipped,
    install: installController.getStatus().surface,
  });

  await runCabinet(ctx, input);
}

void boot();

export {};
